import React from 'react';

import { EnvItem } from 'features/diagnostics/components/env-item';
import { useStoreDiagnostics } from 'features/store/hooks/use-store-diagnostics';
import { StyleSheet } from 'react-native';

import { IRestaurant } from '@rbi-ctg/store';
import { ModalContent, ModalHeading } from 'components/modal';
import { Box, Typography } from 'components/ui';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { spacing } from 'styles/constants';

const ContentContainer = ModalContent.withConfig({
  paddingX: {
    base: '2',
    md: '15%',
  },
  paddingTop: {
    base: '0',
    md: '1',
  },
  paddingBottom: {
    md: '8',
  },
});

const StyledModalHeading = ModalHeading.withConfig({
  marginBottom: '6',
});

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Typography bold fontSize="md">
    {children}
  </Typography>
);

export const determineStoreInfoSubheader = (
  storeName: string | undefined,
  storeAddress: string | undefined
) => {
  if (!storeName) {
    return `(${storeAddress})`;
  }

  if (!storeAddress || storeName.toLowerCase() === storeAddress.toLowerCase()) {
    return `(${storeName})`;
  }

  return `(${storeName} - ${storeAddress})`;
};

export const DiagnosticModalContents = ({ store }: { store: IRestaurant }) => {
  const isDiagnosticsEnabled = useFlag(LaunchDarklyFlag.ENABLE_INTERNAL_DIAGNOSTIC_TOOLS);
  const getStoreDiagnostics = useStoreDiagnostics();
  const diagnostics = getStoreDiagnostics(store);

  if (!isDiagnosticsEnabled || !diagnostics) {
    return (
      <ContentContainer>
        <StyledModalHeading>No Diagnostic Data Available</StyledModalHeading>
        <p>Sorry, diagnostic data is not available at this time!</p>
      </ContentContainer>
    );
  }
  const {
    storeName,
    storeStreetAddress,
    isStoreAvailableForPickup,
    storeLocalTime,
    storeServiceModeHours,
    storeServiceModeStatuses,
    storeDayPartHours,
    storeHasMobileOrdering,
    storeId,
    storePosVendor,
    storeMobileOrderingStatus,
    isStoreAvailableForDelivery,
    storeAvailableDayParts,
    isMobileOrderingAvailable,
    isPOSAvailable,
    isOneMenu,
  } = diagnostics;

  return (
    <ContentContainer>
      <SectionTitle>General</SectionTitle>
      <Box style={styles.row}>
        <Box style={styles.leftColumn}>
          <EnvItem envKey={'Store Id'} value={storeId} />
        </Box>
        <Box style={styles.rightColumn}>
          <EnvItem envKey={'POS Vendor'} value={storePosVendor} />
        </Box>
      </Box>

      <EnvItem envKey={'Store Name'} value={storeName} />
      <EnvItem envKey={'Store Street Address'} value={storeStreetAddress} />
      <EnvItem envKey={'isOneMenu'} value={isOneMenu ? 'YES' : 'NO'} />

      <Box style={styles.sectionSpacer} />

      <SectionTitle>Availability</SectionTitle>
      <EnvItem
        envKey={'isStoreAvailableForPickup'}
        value={isStoreAvailableForPickup ? 'YES' : 'NO'}
        infoMessage="Store will show as Open in the store locator"
      />

      <EnvItem
        envKey={'isStoreAvailableForDelivery'}
        value={isStoreAvailableForDelivery ? 'YES' : 'NO'}
        infoMessage="Store will be used to deliver food if its close enough to the user"
      />
      <EnvItem
        envKey={'Available DayParts'}
        value={storeAvailableDayParts}
        infoMessage="Shows what dayParts are available"
      />
      <Box style={styles.sectionSpacer} />

      <SectionTitle>Availability Details</SectionTitle>

      <Box style={styles.row}>
        <Box style={styles.leftColumn}>
          <EnvItem
            envKey={'hasMobileOrdering'}
            value={storeHasMobileOrdering ? 'YES' : 'NO'}
            infoMessage="This means this store can accept mobile orders"
          />
        </Box>
        <Box style={styles.rightColumn}>
          <EnvItem
            envKey={'mobileOrderingStatus'}
            value={storeMobileOrderingStatus}
            infoMessage="If it is any of alpha, beta, live or dev the store store can accept mobile orders"
          />
        </Box>
      </Box>

      <EnvItem
        envKey={'isMobileOrderingAvailable'}
        value={isMobileOrderingAvailable ? 'YES' : 'NO'}
        infoMessage="Combines the above flags to determine if the store store can accept mobile orders"
      />
      <EnvItem
        envKey={'isPosAvailable'}
        value={isPOSAvailable ? 'YES' : 'NO'}
        infoMessage="This may be override by restaurantAvailabilityOverride flag"
      />

      <EnvItem envKey={'serviceModeStatuses'} value={JSON.stringify(storeServiceModeStatuses)} />

      <EnvItem envKey={'Time at the store'} value={storeLocalTime} />
      <EnvItem
        envKey={'serviceModeHours'}
        value={JSON.stringify(storeServiceModeHours)}
        infoMessage="This store should be serving breakfast"
      />

      <EnvItem envKey={'Time at the store'} value={storeLocalTime} />
      <EnvItem
        envKey={'dayPartHours'}
        value={JSON.stringify(storeDayPartHours)}
        infoMessage="This store should be serving breakfast"
      />
    </ContentContainer>
  );
};

const styles = StyleSheet.create({
  leftColumn: {
    paddingRight: spacing.$2,
    width: '50%',
  },
  rightColumn: {
    paddingLeft: spacing.$2,
    width: '50%',
  },
  row: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  sectionSpacer: {
    marginBottom: spacing.$3,
  },
});
